/**
 * Pure grouping, sorting, and label helpers for the home page's Active /
 * Shared / Archived / Deleted sections (LEV-2014). No React, no browser
 * globals: this file is imported from a server component, so it must stay
 * importable there. Invariants: a deleted project always lands in "deleted"
 * even when it is also archived (deleted wins); "shared" runs both
 * directions (the viewer owns a project someone else was invited to, or the
 * viewer was invited to someone else's); and a missing viewer id (no auth
 * context) makes every live row "active", since the owned/collaborator split
 * needs a viewer to compare against.
 */

import { DAY_MS, PROJECT_PURGE_WINDOW_DAYS } from "@/constants/projectLifecycle";
import { formatShortDate } from "@/lib/utils/shared";
import type { LandingProjectListItem } from "./LandingProjects";

export type LandingSectionKey = "active" | "shared" | "archived" | "deleted";

export type LandingProjectGroups = Record<LandingSectionKey, LandingProjectListItem[]>;

function toTime(date: Date | string): number {
  return typeof date === "string" ? new Date(date).getTime() : date.getTime();
}

function sortByDesc(
  items: LandingProjectListItem[],
  key: (item: LandingProjectListItem) => Date | string,
): LandingProjectListItem[] {
  return [...items].sort((a, b) => toTime(key(b)) - toTime(key(a)));
}

export function groupLandingProjects(
  items: LandingProjectListItem[],
  viewerUserId: string | undefined,
  opts: { showDeleted: boolean },
): LandingProjectGroups {
  const groups: LandingProjectGroups = { active: [], shared: [], archived: [], deleted: [] };

  for (const item of items) {
    if (item.deletedAt) {
      if (opts.showDeleted) groups.deleted.push(item);
      continue;
    }
    if (item.archivedAt) {
      groups.archived.push(item);
      continue;
    }
    // No viewer: nothing can be "owned", so the collaborator split (which
    // needs a viewer to compare ownerId against) never applies here.
    if (viewerUserId === undefined) {
      groups.active.push(item);
      continue;
    }
    const isOwner = item.ownerId === viewerUserId;
    if (!isOwner || item._count.collaborators > 0) {
      groups.shared.push(item);
    } else {
      groups.active.push(item);
    }
  }

  groups.active = sortByDesc(groups.active, (p) => p.updatedAt);
  groups.shared = sortByDesc(groups.shared, (p) => p.updatedAt);
  groups.archived = sortByDesc(groups.archived, (p) => p.archivedAt as Date | string);
  groups.deleted = sortByDesc(groups.deleted, (p) => p.deletedAt as Date | string);

  return groups;
}

interface CollaboratorAccessItem {
  collaborators: Array<{ user: { name: string | null } }>;
  _count: { collaborators: number };
}

/** "A", "A and B", or "A, B and C" (no Oxford comma) for 1-3+ parts. */
function joinWithAnd(parts: string[]): string {
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/**
 * Trimmed, non-empty names from a card's capped collaborator sample. Shared
 * by `collaboratorAccessLine` and `sharedWithLine` so the two lines never
 * disagree about which collaborators count as "named".
 */
function collaboratorNames(item: CollaboratorAccessItem): string[] {
  return item.collaborators
    .map((c) => c.user.name?.trim())
    .filter((n): n is string => Boolean(n));
}

/**
 * "X also has access" line for a card's collaborators. `_count.collaborators`
 * is the true accepted-collaborator total; `collaborators` is a capped sample
 * (callers pass at most 3) and can be SHORTER than the total it names, so the
 * line must key every decision off the count, never `.length`. Names every
 * fetched collaborator that has a usable name, then appends "and N other(s)"
 * for whatever the sample did not cover PLUS any nameless collaborator in the
 * sample (5 collaborators, 3 names -> "Ben, Priya, Sam and 2 others also have
 * access."). The "other" count is clamped at 0, so a sample longer than the
 * total (a server mismatch) never produces a negative "other" count. Falls
 * back to "N people also have access." when no sampled collaborator has a
 * usable name (empty sample, or every name blank) but the count is not zero.
 * "also has access" (singular) only when exactly one person is named and
 * nothing remains uncounted. No email fallback — `projects.list` never sends
 * collaborator emails to the client (every viewer sees this list, not just
 * the owner) — so a nameless collaborator is counted, never called
 * "Someone". Null at zero so callers can render nothing rather than an empty
 * line.
 */
export function collaboratorAccessLine(item: CollaboratorAccessItem): string | null {
  const total = item._count.collaborators;
  if (total === 0) return null;

  const names = collaboratorNames(item);
  if (names.length === 0) {
    return total === 1 ? "1 person also has access." : `${total} people also have access.`;
  }

  const othersCount = Math.max(0, total - names.length);
  const parts = [...names];
  if (othersCount > 0) {
    parts.push(`${othersCount} other${othersCount === 1 ? "" : "s"}`);
  }

  const isSingular = names.length === 1 && othersCount === 0;
  return `${joinWithAnd(parts)} also ${isSingular ? "has" : "have"} access.`;
}

/**
 * "Shared with …" byline for a card the viewer owns, shown alongside
 * `collaboratorAccessLine`'s dialog copy rather than instead of it — same
 * name/count rules (count-driven, capped-sample tolerant, nameless
 * collaborators counted not dropped), but phrased for the card footer and
 * with no trailing period, since the caller appends " · Edited …" after it.
 * Null at zero so an unshared owned card renders no "Shared with" segment.
 */
export function sharedWithLine(item: CollaboratorAccessItem): string | null {
  const total = item._count.collaborators;
  if (total === 0) return null;

  const names = collaboratorNames(item);
  if (names.length === 0) {
    return total === 1 ? "Shared with 1 person" : `Shared with ${total} people`;
  }

  const othersCount = Math.max(0, total - names.length);
  const parts = [...names];
  if (othersCount > 0) {
    parts.push(`${othersCount} other${othersCount === 1 ? "" : "s"}`);
  }

  return `Shared with ${joinWithAnd(parts)}`;
}

export function cardRole(
  item: Pick<LandingProjectListItem, "ownerId">,
  viewerUserId: string | undefined,
): "owner" | "collaborator" {
  return item.ownerId === viewerUserId ? "owner" : "collaborator";
}

/**
 * True once a deleted project is on its way out for good: the founder asked
 * ("Delete now", `purgeRequestedAt`) or the purge job claimed it before its
 * first irreversible delete (`purgeStartedAt`). Either marker makes the
 * restore window meaningless, so the card drops Restore and Delete now and
 * stops counting days. One predicate, because the chip, the menu and the
 * click target must never disagree about it.
 */
export function isPurgePending(
  item: Pick<LandingProjectListItem, "purgeRequestedAt" | "purgeStartedAt">,
): boolean {
  return item.purgeRequestedAt != null || item.purgeStartedAt != null;
}

/**
 * Days left before the purge job removes a deleted project. Must floor the
 * same way the server's `restore` procedure computes `deletedForDays`
 * (`src/lib/api/routers/projects.ts`) or the UI and the restore guard
 * disagree about which day a project stops being restorable.
 */
export function purgeDaysLeft(deletedAt: Date | string, now: number): number {
  const ageMs = now - toTime(deletedAt);
  const daysLeft = PROJECT_PURGE_WINDOW_DAYS - Math.floor(ageMs / DAY_MS);
  return Math.max(0, daysLeft);
}

/** "27 days left", "1 day left", or "less than a day left" at the floor. */
export function purgeDaysLeftLabel(daysLeft: number): string {
  if (daysLeft <= 0) return "less than a day left";
  if (daysLeft === 1) return "1 day left";
  return `${daysLeft} days left`;
}

/**
 * "27 more days", "1 more day", or "less than a day" at the floor — dialog
 * body phrasing (vs. `purgeDaysLeftLabel`'s chip-shaped "27 days left"). Never
 * say "0 more days": at the floor the window is closer to gone than open.
 */
export function restoreWindowPhrase(daysLeft: number): string {
  if (daysLeft <= 0) return "less than a day";
  if (daysLeft === 1) return "1 more day";
  return `${daysLeft} more days`;
}

/**
 * "today" / "yesterday" / "N days ago" / "N week(s) ago" up to 27 days, then
 * a plain short date with no "ago" prefix so an old archive or delete date
 * reads as a point in time rather than a vague duration.
 */
export function formatDaysAgo(date: Date | string, now: number): string {
  const ageMs = now - toTime(date);
  const days = Math.floor(ageMs / DAY_MS);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 28) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  }
  return formatShortDate(date);
}
